import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Scroll, Swords, CheckCircle, XCircle, Mail, Skull, Trash2, PenSquare, Send, Hammer, Shield, Users, UserPlus, UserCheck, Share2, Search, X, Bookmark, BookmarkCheck, Pickaxe } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import ShareModal from "@/components/ShareModal";
import FriendChats from "@/components/FriendChats";
import BattleReplay from "@/components/BattleReplay";

// Custom Inbox Icon
const InboxIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/inbox-icon.png" 
    alt="Inbox" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

const TYPE_META = {
  challenge_request: { icon: Swords, color: "text-red-400", label: "Challenge" },
  challenge_declined: { icon: XCircle, color: "text-orange-400", label: "Declined" },
  battle_result: { icon: CheckCircle, color: "text-emerald-400", label: "Result" },
  duel_replay: { icon: Swords, color: "text-amber-300", label: "Daily Duel" },
  retirement: { icon: Skull, color: "text-zinc-400", label: "Retirement" },
  equipment_destroyed: { icon: Hammer, color: "text-amber-400", label: "Blacksmith" },
  alliance: { icon: Shield, color: "text-amber-300", label: "Alliance" },
  friend_request: { icon: UserPlus, color: "text-amber-300", label: "Friend Request" },
  friend_accepted: { icon: UserCheck, color: "text-emerald-400", label: "Friend" },
  player_message: { icon: Mail, color: "text-sky-400", label: "Message" },
  welcome: { icon: Scroll, color: "text-amber-300", label: "Welcome" },
  chat_mention: { icon: Mail, color: "text-sky-300", label: "Chat Mention" },
  mining: { icon: Pickaxe, color: "text-amber-300", label: "Mining" },
};

// Format timestamp to exact date and time
const formatExactDateTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

// Which inbox section each message type belongs to.
const CATEGORY = {
  challenge_request: "challenges",
  challenge_declined: "challenges",
  battle_result: "battles",
  duel_replay: "battles",
};
const catOf = (m) => CATEGORY[m.type] || "messages";

const WELCOME_LINKS = [
  { pre: "Start by visiting the ", to: "/recruit", label: "recruit pool", post: " and recruit your stable of gladiators!" },
  { pre: "Set your ", to: "/training", label: "training schedule", post: " to make your gladiator stronger!" },
  { pre: "Go to the ", to: "/blacksmith", label: "blacksmith", post: " and collect your free fragments!" },
  { pre: " - Use them to forge your first pieces of equipment!", hideIcon: true },
  { pre: "Go to ", to: "/challenges", label: "The Arena page", post: " to fight other gladiators!" },
  { pre: " - Earn more fragments by competing in the arena! Use these to forge equipment!", hideIcon: true },
  { pre: " - You will likely lose most of your early battles, don't worry about it! Winning will come with more training!", hideIcon: true },
  { pre: "Participate in events, tournaments, the sands & complete quests even as a beginner on the ", to: "/events", label: "Gladiator Trials page", post: "!" },
  { pre: "Join an ", to: "/alliance", label: "alliance", post: " to team up with other lanistae!" },
  { pre: "Visit ", to: "/chat", label: "arena chat", post: " to ask for help or talk to other lanistae!" },
  { pre: "Once you've established yourself a little bit, consider building a ", to: "/ludus", label: "vivarium", post: "!" },
  { pre: " - You can then capture beasts to fight along side your gladiator!", hideIcon: true },
  { pre: " - You can also breed them to create stronger versions of your beast!", hideIcon: true },
];


// Battle Rewards Display Component
function BattleRewards({ battle, userId }) {
  if (!battle || !battle.salvage || !userId) return null;
  
  const userRewards = battle.salvage[userId];
  if (!userRewards) return null;

  const hasRewards = userRewards.follis > 0 || 
    Object.values(userRewards.weapon || {}).some(v => v > 0) ||
    Object.values(userRewards.armor || {}).some(v => v > 0);

  if (!hasRewards) return null;

  const rarityColors = {
    common: 'text-gray-400',
    uncommon: 'text-green-400',
    rare: 'text-blue-400',
    elite: 'text-purple-400',
    legendary: 'text-orange-400'
  };

  return (
    <div className="bg-amber-900/10 border border-amber-900/30 rounded-lg p-4 mt-4">
      <h4 className="text-amber-500 font-semibold mb-3 flex items-center gap-2">
        <Hammer className="w-4 h-4" />
        Your Rewards
      </h4>
      
      <div className="space-y-2">
        {/* Follis */}
        {userRewards.follis > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Follis:</span>
            <span className="text-amber-400 font-semibold">+{userRewards.follis}</span>
          </div>
        )}

        {/* Weapon Fragments */}
        {userRewards.weapon && Object.entries(userRewards.weapon).map(([rarity, count]) => (
          count > 0 && (
            <div key={`weapon-${rarity}`} className="flex items-center justify-between text-sm">
              <span className="text-gray-400 capitalize">{rarity} Weapon Fragments:</span>
              <span className={`${rarityColors[rarity]} font-semibold`}>+{count}</span>
            </div>
          )
        ))}

        {/* Armor Fragments */}
        {userRewards.armor && Object.entries(userRewards.armor).map(([rarity, count]) => (
          count > 0 && (
            <div key={`armor-${rarity}`} className="flex items-center justify-between text-sm">
              <span className="text-gray-400 capitalize">{rarity} Armor Fragments:</span>
              <span className={`${rarityColors[rarity]} font-semibold`}>+{count}</span>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function WelcomeMessage({ onNavigate }) {
  return (
    <div data-testid="welcome-message" className="text-sm leading-relaxed text-zinc-200">
      <p className="font-cinzel text-amber-300 text-base mb-3">Welcome to your Lanista Manager ludus!</p>
      <p className="text-zinc-300 italic mb-4 border-b border-amber-900/30 pb-3">
        You have entered a world of blood and sand, where the only currency that matters is the roar of the crowd. Listen to me: some of you will not make it, and many of you will be scarred. For those who prove their worth and conquer the sands, there is glory. Thrust your blade into another man&apos;s flesh, and this crowd will applaud and love you for it. Win the crowd, and you win your freedom. Behold the dust of the Colosseum. What you do in this arena... echoes in eternity!
      </p>
      <ul className="space-y-2 mb-4">
        {WELCOME_LINKS.map((l, i) => (
          <li key={l.to || i} className="flex gap-2">
            {!l.hideIcon && <span className="text-amber-500/70">⚔</span>}
            {l.hideIcon && <span className="w-4"></span>}
            <span>
              {l.pre}
              {l.to && (
                <button onClick={() => onNavigate(l.to)} data-testid={`welcome-link-${l.to.slice(1)}`}
                  className="text-amber-400 hover:text-amber-300 underline font-medium">{l.label}</button>
              )}
              {l.post}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-zinc-300 font-medium pt-3 border-t border-amber-900/30">
        Good luck out there, Lanista! You have a tough road ahead of you!
      </p>
    </div>
  );
}

const TABS = [
  { id: "messages", label: "Messages", icon: Mail },
  { id: "sent", label: "Sent", icon: Send },
  { id: "challenges", label: "The Arena", icon: Swords },
  { id: "battles", label: "Battle Results", icon: CheckCircle },
  { id: "friends", label: "Friend Chats", icon: Users },
];

const renderTextWithMentions = (text, managers, navigate, setOpen = null, messageId = '') => {
  if (!text) return null;
  const markdownRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIdx = 0;
  let match;
  const parseMentions = (subtext, baseKey) => {
    const mentionRegex = /@([a-zA-Z0-9_\-]+)/g;
    const subParts = [];
    let subLastIdx = 0;
    let subMatch;
    while ((subMatch = mentionRegex.exec(subtext)) !== null) {
      const full = subMatch[0];
      const handle = subMatch[1];
      const idx = subMatch.index;
      if (idx > subLastIdx) {
        subParts.push(<span key={`${messageId}-${baseKey}-t-${subLastIdx}`}>{subtext.substring(subLastIdx, idx)}</span>);
      }
      const matchedUser = (managers || []).find(
        (m) => m.name.toLowerCase() === handle.toLowerCase()
      );
      if (matchedUser) {
        subParts.push(
          <button
            key={`${messageId}-${baseKey}-m-${matchedUser.user_id}-${idx}`}
            onClick={() => {
              if (setOpen) setOpen(null);
              navigate(`/profile/${matchedUser.user_id}`);
            }}
            className="text-amber-400 hover:text-amber-300 underline font-medium"
          >
            {full}
          </button>
        );
      } else {
        subParts.push(<span key={`${messageId}-${baseKey}-m-${idx}`}>{full}</span>);
      }
      subLastIdx = mentionRegex.lastIndex;
    }
    if (subLastIdx < subtext.length) {
      subParts.push(<span key={`${messageId}-${baseKey}-t-${subLastIdx}`}>{subtext.substring(subLastIdx)}</span>);
    }
    return subParts.length > 0 ? subParts : subtext;
  };
  let partKey = 0;
  while ((match = markdownRegex.exec(text)) !== null) {
    const [full, label, url] = match;
    const idx = match.index;
    if (idx > lastIdx) {
      parts.push(...[].concat(parseMentions(text.substring(lastIdx, idx), partKey++)));
    }
    parts.push(
      <button
        key={`${messageId}-link-${partKey++}`}
        onClick={() => {
          if (setOpen) setOpen(null);
          navigate(url);
        }}
        className="text-amber-400 hover:text-amber-300 underline font-medium"
      >
        {label}
      </button>
    );
    lastIdx = markdownRegex.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(...[].concat(parseMentions(text.substring(lastIdx), partKey++)));
  }
  return parts.length > 0 ? parts : text;
};

export default function Inbox() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sentData, setSentData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [friendsData, setFriendsData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [tab, setTab] = useState("messages");
  const [open, setOpen] = useState(null);        // message being read
  const [battleReplayId, setBattleReplayId] = useState(null);  // For showing battle replay modal
  const [battleReplayData, setBattleReplayData] = useState(null);  // Store full battle data for rewards
  const [shareBattleId, setShareBattleId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [to, setTo] = useState("");
  const [toName, setToName] = useState("");      // Display name of selected recipient
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const searchTimeoutRef = useRef(null);

  const openBattleReplay = async (battleId) => {
    if (!battleId) return;
    setBattleReplayId(battleId);
    try {
      const battleRes = await api.get(`/battles/${battleId}`);
      setBattleReplayData(battleRes.data);
    } catch (err) {
      console.error('Failed to fetch battle data:', err);
      setBattleReplayData(null);
    }
  };

  const load = () => api.get("/inbox").then((r) => setData(r.data)).catch(() => setData({ messages: [], unread: 0 }));
  const loadSent = () => api.get("/inbox/sent").then((r) => setSentData(r.data)).catch(() => setSentData({ messages: [] }));
  const loadFriends = () => api.get("/friends").then((r) => setFriendsData(r.data)).catch(() => {});

  // Debounced search for lanistas
  const searchLanistas = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const { data } = await api.get(`/managers/search?q=${encodeURIComponent(query)}`);
      setSearchResults(data.managers || []);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setShowResults(true);
    // Clear previous timeout
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => searchLanistas(value), 300);
  };

  const selectRecipient = (userId, name) => {
    setTo(userId);
    setToName(name);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  const clearRecipient = () => {
    setTo("");
    setToName("");
    setSearchQuery("");
    setSearchResults([]);
  };

  const tabInit = useRef(false);
  useEffect(() => {
    // On first load, open whichever section has unread items (messages > challenges > battles).
    if (!data || tabInit.current) return;
    tabInit.current = true;
    const g = { messages: [], challenges: [], battles: [] };
    (data.messages || []).forEach((m) => g[catOf(m)].push(m));
    const firstUnread = TABS.find((t) => g[t.id] && g[t.id].some((m) => !m.read));
    if (firstUnread) setTab(firstUnread.id);
  }, [data]);

  useEffect(() => {
    load();
    loadSent();
    loadFriends();
    api.get("/managers").then((r) => setManagers(r.data.managers)).catch(() => {});
  }, []);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (requestedTab && ["messages", "challenges", "battles", "friends", "sent"].includes(requestedTab)) {
      setTab(requestedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    const battleId = searchParams.get("battle");
    if (!battleId) return;
    openBattleReplay(battleId);
    const next = new URLSearchParams(searchParams);
    next.delete("battle");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Pre-open compose when arriving from a profile "Message" button
  useEffect(() => {
    if (location.state?.to) {
      const recipientId = location.state.to;
      const recipientName = location.state.toName || "";
      setTo(recipientId);
      setToName(recipientName);
      setSearchQuery("");
      setSubject("");
      setBody("");
      setComposeOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line

  const openMsg = async (m) => {
    // For battle results, open the battle replay and fetch battle data
    if (["battle_result", "duel_replay"].includes(m.type) && (m.battle_id || m.ref)) {
      await openBattleReplay(m.battle_id || m.ref);
      if (!m.read) { await api.post(`/inbox/${m.id}/read`); load(); }
      return;
    }
    
    // For other messages, open the normal modal
    setOpen(m);
    if (!m.read) { await api.post(`/inbox/${m.id}/read`); load(); }
  };

  const del = async (e, msg) => {
    e.stopPropagation();
    
    // If message is saved, show confirmation
    if (msg.saved) {
      setMessageToDelete(msg);
      setDeleteConfirmOpen(true);
      return;
    }
    
    // If not saved, delete immediately
    try { 
      await api.delete(`/inbox/${msg.id}`); 
      toast.success("Message deleted"); 
      load(); 
    } catch (err) { 
      toast.error(apiErr(err)); 
    }
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;
    
    try {
      await api.delete(`/inbox/${messageToDelete.id}`);
      toast.success("Saved message deleted");
      load();
      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
      if (open?.id === messageToDelete.id) {
        setOpen(null);
      }
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const toggleSave = async (e, msg) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/inbox/${msg.id}/save`);
      toast.success(data.saved ? "Message saved" : "Message unsaved");
      
      // Update the open modal message if it's the one being saved
      if (open && open.id === msg.id) {
        setOpen({ ...open, saved: data.saved });
      }
      
      load();
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const clearCurrent = async () => {
    setBusy(true);
    try {
      if (tab === "friends") {
        await api.delete("/friends/chat/clear");
        toast.success("Your friend chats were cleared on your side");
        setClearOpen(false); loadFriends();
      } else if (tab === "sent") {
        const { data: res } = await api.delete("/inbox/sent");
        toast.success(res.deleted ? `Cleared ${res.deleted} sent message${res.deleted > 1 ? "s" : ""}` : "Nothing to clear");
        setClearOpen(false); setOpen(null); loadSent();
      } else {
        const { data: res } = await api.delete(`/inbox?category=${tab}`);
        toast.success(res.deleted ? `Cleared ${res.deleted} item${res.deleted > 1 ? "s" : ""}` : "Nothing to clear");
        setClearOpen(false); setOpen(null); load();
      }
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const send = async () => {
    if (!to) return toast.error("Choose a recipient");
    if (!body.trim()) return toast.error("Write a message");
    setBusy(true);
    try {
      const { data: res } = await api.post("/messages", { to_user: to, subject, body });
      toast.success(res.routed === "friend_chat" ? "Sent to your Friend Chat" : "Message sent");
      setComposeOpen(false); 
      setTo(""); 
      setToName("");
      setSearchQuery("");
      setSubject(""); 
      setBody("");
      if (res.routed === "friend_chat") loadFriends();
      else loadSent(); // Reload sent messages after sending
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data || !sentData) return <Loader />;

  const messages = data.messages || [];
  const sentMessages = sentData.messages || [];
  const groups = { messages: [], challenges: [], battles: [], sent: sentMessages };
  messages.forEach((m) => groups[catOf(m)].push(m));
  const friendUnread = (friendsData.friends || []).reduce((n, f) => n + (f.unread || 0), 0);
  const friendCount = (friendsData.friends || []).length;
  const requestCount = (friendsData.incoming || []).length;
  const unreadOf = (id) => id === "friends" ? friendUnread + requestCount : id === "sent" ? 0 : groups[id].filter((m) => !m.read).length;
  const countOf = (id) => id === "friends" ? friendCount : groups[id].length;
  const shown = groups[tab] || [];
  const clearable = tab === "friends" ? friendCount > 0 : tab === "sent" ? sentMessages.length > 0 : (groups[tab] || []).length > 0;
  const tabLabel = TABS.find((t) => t.id === tab)?.label || "Inbox";

  return (
    <div>
      <PageHeader title="Inbox/Friends" subtitle={`${data.unread || 0} unread · ${friendCount} friend${friendCount === 1 ? "" : "s"}`} icon={InboxIcon}
        action={<div className="flex items-center gap-2">
          {clearable && (
            <button data-testid="clear-inbox-btn" className={btn("ghost")} onClick={() => setClearOpen(true)}>
              <span className="flex items-center gap-2"><Trash2 size={15} /> Clear this inbox</span>
            </button>
          )}
          <button data-testid="compose-msg-btn" className={btn()} onClick={() => { setComposeOpen(true); }}>
            <span className="flex items-center gap-2"><PenSquare size={15} /> New Message</span>
          </button>
        </div>} />

      {/* Section tabs */}
      <div data-testid="inbox-tabs" className="flex flex-wrap gap-2 mb-5 border-b border-zinc-800/70">
        {TABS.map((t) => {
          const cnt = countOf(t.id);
          const unread = unreadOf(t.id);
          const activeTab = tab === t.id;
          return (
            <button key={t.id} data-testid={`inbox-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-cinzel uppercase tracking-wide transition-colors ${
                activeTab ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              <t.icon size={15} className={activeTab ? "text-amber-400" : ""} />
              {t.label}
              <span className="text-zinc-600 normal-case tracking-normal">({cnt})</span>
              {unread > 0 && (
                <span data-testid={`inbox-tab-unread-${t.id}`}
                  className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold">
                  {unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "friends" ? (
        <FriendChats data={friendsData} reload={loadFriends} />
      ) : shown.length === 0 ? (
        <Empty label={
          tab === "challenges" ? "No challenge notifications. Incoming and declined challenges appear here."
          : tab === "battles" ? "No battle results yet. Win or lose a ranked fight and the report lands here."
          : tab === "sent" ? "No sent messages yet. Messages you send to other players will appear here."
          : "No messages. Notes from other lanistae, your alliance, and the arena appear here."} />
      ) : (
        <div className="space-y-2" data-testid={`inbox-list-${tab}`}>
          {shown.map((m) => {
            const t = TYPE_META[m.type] || TYPE_META.player_message;
            return (
              <div key={m.id} data-testid={`inbox-msg-${m.id}`} onClick={() => openMsg(m)}
                className={`group w-full text-left flex items-start gap-3 px-4 py-3 border transition-colors cursor-pointer ${
                  m.read ? "border-zinc-800 bg-zinc-950 hover:border-zinc-700" : "border-amber-700/40 bg-amber-900/10"}`}>
                <t.icon size={18} className={`${t.color} mt-0.5 shrink-0`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-manrope text-zinc-100 truncate">{m.subject}</span>
                      {m.saved && (
                        <BookmarkCheck size={14} className="text-amber-400 shrink-0" title="Saved" />
                      )}
                    </div>
                    {!m.read && <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
                  </div>
                  <p className="text-sm text-zinc-500 whitespace-pre-line line-clamp-2">{m.body}</p>
                  {m.type === "duel_replay" && m.battle_id && <button data-testid={`watch-daily-duel-${m.id}`} onClick={(event) => { event.stopPropagation(); openBattleReplay(m.battle_id); }} className="mt-2 text-xs font-cinzel uppercase tracking-widest text-amber-300 transition-colors hover:text-amber-200">Watch Daily Duel</button>}
                  <span className="text-[10px] uppercase tracking-widest text-zinc-600">{t.label} · {m.from_name} · {formatExactDateTime(m.created_at)}</span>
                </div>
                <button 
                  data-testid={`save-msg-${m.id}`} 
                  onClick={(e) => toggleSave(e, m)}
                  className={`transition-colors shrink-0 p-1 ${m.saved ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-600 hover:text-amber-400'}`}
                  title={m.saved ? "Unsave message" : "Save message"}>
                  {m.saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                </button>
                <button data-testid={`delete-msg-${m.id}`} onClick={(e) => del(e, m)}
                  className="text-zinc-600 hover:text-red-400 transition-colors shrink-0 p-1" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Read message modal */}
      <Modal open={!!open} onClose={() => setOpen(null)} title={open?.subject || "Message"}>
        {open && (
          <div>
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
              <span className="uppercase tracking-widest">{(TYPE_META[open.type] || TYPE_META.player_message).label}</span>
              <span>· from {open.from_name}</span>
              <span>· {formatExactDateTime(open.created_at)}</span>
            </div>
            {open.type === "welcome" ? (
              <WelcomeMessage onNavigate={(to) => { setOpen(null); navigate(to); }} />
            ) : (
              <p className="text-zinc-200 whitespace-pre-line text-sm leading-relaxed">{renderTextWithMentions(open.body, managers, navigate, setOpen, open.id)}</p>
            )}
            <div className="flex gap-2 mt-6">
              <button 
                data-testid="modal-save-btn"
                className={btn("ghost") + " flex-1"} 
                onClick={(e) => toggleSave(e, open)}>
                {open.saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                {open.saved ? "Unsave" : "Save"}
              </button>
              {open.type === "challenge_request" && (
                <button className={btn() + " flex-1"} onClick={() => navigate("/challenges")}>View Challenge</button>
              )}
              {open.type === "player_message" && open.from_user && open.from_user !== "system" && (
                <button data-testid="reply-btn" className={btn("ghost") + " flex-1"}
                  onClick={() => { 
                    setTo(open.from_user); 
                    setToName(open.from_name);
                    setSubject(open.subject?.startsWith("Re:") ? open.subject : `Re: ${open.subject}`); 
                    setBody(""); 
                    setSearchQuery("");
                    setOpen(null); 
                    setComposeOpen(true); 
                  }}>
                  Reply
                </button>
              )}
              <button data-testid="modal-delete-btn" className={btn("danger") + " flex-1"} onClick={(e) => { del(e, open); }}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      <ShareModal open={!!shareBattleId} onClose={() => setShareBattleId(null)} kind="battle" id={shareBattleId}
        text="Watch this arena bout on Lanista!" />

      {/* Compose modal */}
      <Modal open={composeOpen} onClose={() => { setComposeOpen(false); clearRecipient(); }} title="New Message">
        <label className="text-[11px] uppercase tracking-widest text-zinc-500">To</label>
        
        {/* Selected recipient display */}
        {to ? (
          <div data-testid="selected-recipient" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-3 flex items-center justify-between">
            <span className="text-zinc-200">{toName}</span>
            <button data-testid="clear-recipient-btn" onClick={clearRecipient} className="text-zinc-500 hover:text-zinc-300 transition-colors" title="Clear">
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="compose-search-input"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowResults(true)}
                placeholder="Search for a lanista..."
                className="w-full bg-zinc-900 border border-zinc-800 pl-10 pr-3 py-2.5 mt-1 mb-3 text-zinc-100 focus:border-amber-600 outline-none"
                autoComplete="off"
              />
            </div>
            
            {/* Search results dropdown */}
            {showResults && searchQuery.length >= 2 && (
              <div data-testid="search-results" className="absolute z-50 w-full bg-zinc-900 border border-zinc-800 max-h-60 overflow-y-auto shadow-xl">
                {searchLoading ? (
                  <div className="px-3 py-2 text-sm text-zinc-500">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-zinc-500">No lanistas found</div>
                ) : (
                  searchResults.map((m) => (
                    <button
                      key={m.user_id}
                      data-testid={`search-result-${m.user_id}`}
                      onClick={() => selectRecipient(m.user_id, m.name)}
                      className="w-full text-left px-3 py-2 text-zinc-200 hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-b-0"
                    >
                      {m.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <label className="text-[11px] uppercase tracking-widest text-zinc-500">Subject</label>
        <input data-testid="compose-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={80} placeholder="Subject (optional)"
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-3 text-zinc-100 focus:border-amber-600 outline-none" />

        <label className="text-[11px] uppercase tracking-widest text-zinc-500">Message</label>
        <textarea data-testid="compose-body-input" value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} rows={5} placeholder="Write your message…"
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-3 text-zinc-100 focus:border-amber-600 outline-none resize-none" />

        <button data-testid="send-msg-btn" disabled={busy} className={btn() + " w-full"} onClick={send}>
          <span className="flex items-center justify-center gap-2"><Send size={15} /> Send</span>
        </button>
      </Modal>

      {/* Clear-this-inbox confirmation modal */}
      <Modal open={clearOpen} onClose={() => setClearOpen(false)} title={`Clear ${tabLabel}?`}>
        <p className="text-sm text-zinc-400">
          {tab === "friends"
            ? <>This will clear <span className="text-amber-300">all your friend conversations on your side only</span>. Your friends keep their copy of the history. This cannot be undone.</>
            : <>This will permanently delete all items in your <span className="text-amber-300">{tabLabel}</span> inbox. This cannot be undone.</>}
        </p>
        <div className="flex gap-2 mt-6">
          <button data-testid="cancel-clear-btn" className={btn("ghost") + " flex-1"} onClick={() => setClearOpen(false)}>Cancel</button>
          <button data-testid="confirm-clear-btn" disabled={busy} className={btn("danger") + " flex-1"} onClick={clearCurrent}>
            <span className="flex items-center justify-center gap-2"><Trash2 size={15} /> Clear {tabLabel}</span>
          </button>
        </div>
      </Modal>
      
      {/* Battle replay modal for battle result messages */}
      <BattleReplay 
        battleId={battleReplayId}
        battle={battleReplayData}
        open={!!battleReplayId} 
        onClose={() => {
          setBattleReplayId(null);
          setBattleReplayData(null);
        }}
        title="Battle Result"
        shareable={true}
        rewards={battleReplayData && user ? (
          <BattleRewards battle={battleReplayData} userId={user.user_id} />
        ) : null}
      />

      {/* Delete Saved Message Confirmation Modal */}
      {deleteConfirmOpen && messageToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="bg-zinc-900 border border-amber-900/50 rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-cinzel text-amber-300 mb-3 flex items-center gap-2">
              <Bookmark size={20} className="text-amber-400" />
              Delete Saved Message?
            </h3>
            <p className="text-sm text-zinc-300 mb-4">
              This is a saved message. Are you sure you want to delete it?
            </p>
            <div className="bg-zinc-800/50 border border-zinc-700 rounded p-3 mb-4">
              <p className="text-xs text-zinc-400 mb-1">Subject:</p>
              <p className="text-sm text-zinc-200 truncate">{messageToDelete.subject}</p>
            </div>
            <div className="flex gap-3">
              <button 
                className={btn("ghost") + " flex-1"}
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setMessageToDelete(null);
                }}>
                Cancel
              </button>
              <button 
                className={btn("danger") + " flex-1"}
                onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
